import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { t } from "ttag";

import { templates } from "@reactivated";
import type { RichTextBlock as RichTextBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

const offerDetailBodyComponents = {
    text: (block: { value: Immutable<RichTextBlockValue> }) => (
        <RichTextBlock value={block.value} />
    ),
    table: (block: { value: string | null }) =>
        block.value ? (
            <RawHTMLBlock value={block.value} className="block-table" />
        ) : null,
    typed_table: (block: { value: string | null }) =>
        block.value ? <RawHTMLBlock value={block.value} /> : null,
} as const;

const OfferDetailContent = (
    props: Immutable<{ page: templates.OfferDetailPageTemplate["page"] }>,
): React.JSX.Element => (
    <StreamField
        tagName={React.Fragment}
        value={props.page.content.value}
        components={offerDetailBodyComponents}
    />
);

// The breadcrumbs, header and back-link literals are styled by libs'
// _offer-modal.scss when OfferModal embeds this page, so they stay global.
export const OfferDetailPageBody = (
    props: Immutable<Omit<templates.OfferDetailPageTemplate, "page_chrome">>,
): React.JSX.Element => (
    <div
        className={concatClassNames([
            styles.offerDetailsPage,
            "offer-details-page",
            `offer-details-page--${props.page.offer_type}`,
            "u-full-width-title",
            "l-capped-width",
        ])}
    >
        <div className="offer-details-page__breadcrumbs">
            <a href={props.offers_url}>{t`Offers`}</a>
            {props.page.header_text && (
                <>
                    {" > "}
                    <span
                        dangerouslySetInnerHTML={{
                            __html: props.page.header_text,
                        }}
                    />
                </>
            )}
        </div>
        <header className="offer-details-page__header">
            {props.page.pre_header_text && (
                <h3>{props.page.pre_header_text}</h3>
            )}
            {props.page.header_text && (
                <h1
                    dangerouslySetInnerHTML={{
                        __html: props.page.header_text,
                    }}
                />
            )}
        </header>

        <section className={styles.offerDetailsPageContent}>
            <OfferDetailContent page={props.page} />
        </section>
        <div
            className="offer-details-page__back-link"
            role="button"
            tabIndex={0}
            onClick={() => {
                getWindow()?.history.back();
            }}
            onKeyDown={(event) => {
                if (event.code === "Space" || event.code === "Enter") {
                    event.preventDefault();
                    getWindow()?.history.back();
                }
            }}
        >
            {"< "}
            {t`Back`}
        </div>
    </div>
);
